import React, { useEffect, useMemo, useState, useRef } from "react";
import {Button, Card, Col, Form, Modal, Row} from "react-bootstrap";
import GroupService from "../../services/GroupService";
import LeadService from "../../services/LeadService";
import { FiEdit2 } from "react-icons/fi";
import { RiDeleteBinLine } from "react-icons/ri";
import { FaUsers } from "react-icons/fa";
import { API_STATUS } from '../../constants/apiStatusCode';
import { LuImport } from "react-icons/lu";
import MultiSelect from "../../components/common/MultiSelect";


const PAGE_SIZE = 6;
const API_THROTTLE_TIME = 5 * 60 * 1000; // 5 minutes in milliseconds
const MIN_SEARCH_LENGTH = 3;

const getSearchQuery = (value) => {
    const trimmed = (value || "").trim();
    if (!trimmed || trimmed.length < MIN_SEARCH_LENGTH) return undefined;
    return trimmed;
};

const isSearchTooShort = (value) => {
    const trimmed = (value || "").trim();
    return trimmed.length > 0 && trimmed.length < MIN_SEARCH_LENGTH;
};

// Persist throttle timestamps, paging info and cached data across component
// unmounts (e.g. when switching WhatsApp tabs).
let lastGroupsCallTs = 0;
let lastGroupsPageGlobal = 0;
let lastLeadsCallTs = 0;

let cachedGroups = [];
let cachedGroupsTotalCount = 0;
let cachedGroupsPage = 1;
let cachedGroupsSearch = "";

let cachedLeads = [];

// Basic email validation helper
const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

const Group = () => {
    // Initialise state from cached values so that when we throttle API calls
    // after tab changes, the previously loaded data is still shown.
    const [groups, setGroups] = useState(() => cachedGroups);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(() => cachedGroupsPage || 1);
    const [totalCount, setTotalCount] = useState(() => cachedGroupsTotalCount);
    const [search, setSearch] = useState(() => cachedGroupsSearch);

    const [showEditModal, setShowEditModal] = useState(false);
    const [editMode, setEditMode] = useState("create"); // 'create' | 'edit'
    const [currentGroup, setCurrentGroup] = useState(null);

    const [phoneNumber, setPhoneNumber] = useState("");
    const [contactName, setContactName] = useState("");
    const [contactEmail, setContactEmail] = useState("");
    const [addingContact, setAddingContact] = useState(false);
    const [leads, setLeads] = useState(() => cachedLeads);
    const [selectedLeadIds, setSelectedLeadIds] = useState([]);
    const [loadingLeads, setLoadingLeads] = useState(false);

    // Throttling refs to track last API call times and last page.
    // Initialise from module-level variables so values persist across tab changes
    // (component unmounts / remounts).
    const lastGroupsCall = useRef(lastGroupsCallTs);
    const lastGroupsPage = useRef(lastGroupsPageGlobal);
    const lastLeadsCall = useRef(lastLeadsCallTs);

    const totalPages = useMemo(
        () => (totalCount ? Math.ceil(totalCount / PAGE_SIZE) : 1),
        [totalCount]
    );

    useEffect(() => {
        fetchGroups();
    }, [page]);

    const fetchGroups = async (force = false) => {
        const now = Date.now();
        // Only throttle if it's the same page and within throttle time
        const isSamePage = lastGroupsPage.current === page;
        const isSameSearch = search === cachedGroupsSearch;
        if (!force && isSamePage && isSameSearch && (now - lastGroupsCall.current) < API_THROTTLE_TIME) {
            console.log("Groups API call throttled - last call was less than 5 minutes ago");
            return;
        }

        try {
            setLoading(true);
            lastGroupsCall.current = now;
            lastGroupsPage.current = page;
            lastGroupsCallTs = now;
            lastGroupsPageGlobal = page;
            const params = {
                PageNumber: page,
                PageSize: PAGE_SIZE,
                Search: getSearchQuery(search),
            };

            const resp = await GroupService.getAllGroups(params);
            const data = resp?.data?.data;
            const items = data.items || [];
            const mappedGroups = 
                items.map((g) => ({
                    id: g.groupId,
                    name: g.name,
                    description: g.description,
                    contactCount: g.contactCount || g.contacts?.length || 0,
                    contacts: g.contacts || [],
                    createdAt: g.createdOn,
                }));
            setGroups(mappedGroups);
            setTotalCount(data?.totalCount || items.length);

            // Update cache so data is preserved across unmounts
            cachedGroups = mappedGroups;
            cachedGroupsTotalCount = data?.totalCount || items.length;
            cachedGroupsPage = page;
            cachedGroupsSearch = search;
        } catch (e) {
            console.error("Failed to load groups", e);
        } finally {
            setLoading(false);
        }
    };

    const handleSearchChange = (e) => {
        setSearch(e.target.value);
        setPage(1);
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        if (isSearchTooShort(search)) return;
        fetchGroups(true);
    };

    const fetchLeads = async (force = false) => {
        const now = Date.now();
        if (!force && (now - lastLeadsCall.current) < API_THROTTLE_TIME) {
            console.log("Leads API call throttled - last call was less than 5 minutes ago");
            return;
        }

        try {
            setLoadingLeads(true);
            lastLeadsCall.current = now;
            lastLeadsCallTs = now;
            const resp = await LeadService.getLeadsInfo();
            const data = resp?.data?.data || [];
            const nextLeads = Array.isArray(data) ? data : [];
            setLeads(nextLeads);
            cachedLeads = nextLeads;
        } catch (e) {
            console.error("Failed to load leads", e);
            setLeads([]);
        } finally {
            setLoadingLeads(false);
        }
    };

    const handleLeadSelection = (selectedIds) => {
        setSelectedLeadIds(selectedIds);
    };

    const addSelectedLeadsAsContacts = () => {
        if (selectedLeadIds.length === 0) {
            alert("Please select at least one lead");
            return;
        }

        const existingContacts = currentGroup?.contacts || [];
        const existingPhoneNumbers = new Set(
            existingContacts.map(c => c.phoneNumber?.toString())
        );

        const newContacts = selectedLeadIds
            .map(leadId => {
                const lead = leads.find(l => l.leadId === leadId);
                if (!lead) return null;

                const phoneNumber = lead.phoneNumber?.toString() || "";
                const name = lead.name?.trim() || "";

                // Skip if phone number already exists
                if (existingPhoneNumbers.has(phoneNumber)) {
                    return null;
                }

                // Skip if no phone number
                if (!phoneNumber) {
                    return null;
                }

                return {
                    id: Date.now() + Math.random(),
                    phoneNumber: phoneNumber,
                    name: name || `Lead ${phoneNumber}`,
                    email: lead.email || "",
                };
            })
            .filter(Boolean);

        if (newContacts.length === 0) {
            alert("Selected leads are already in the group or don't have phone numbers");
            return;
        }

        setCurrentGroup((prev) => ({
            ...prev,
            contacts: [...existingContacts, ...newContacts],
        }));

        setSelectedLeadIds([]);
        alert(`Added ${newContacts.length} contact(s) from leads`);
    };

    const openCreateModal = () => {
        setEditMode("create");
        setCurrentGroup({
            id: null,
            name: "",
            description: "",
            contacts: [],
        });
        setPhoneNumber("");
        setContactName("");
        setContactEmail("");
        setSelectedLeadIds([]);
        fetchLeads();
        setShowEditModal(true);
    };

    const openEditModal = (group) => {
        setEditMode("edit");
        setCurrentGroup({
            ...group,
            contacts: group.contacts || [],
        });
        setPhoneNumber("");
        setContactName("");
        setContactEmail("");
        setSelectedLeadIds([]);
        fetchLeads();
        setShowEditModal(true);
    };

    const handleDelete = async (group) => {
        if (!window.confirm(`Delete group "${group.name}"?`)) return;
        try {
            await GroupService.deleteGroup(group.id);
            await fetchGroups(true); // Force refresh after delete
        } catch (e) {
            console.error("Failed to delete group", e);
            alert("Failed to delete group");
        }
    };

    const handleSave = async () => {
        if (!currentGroup) return;

        if (!currentGroup.name || !currentGroup.description) {
            alert("Please fill in all required fields");
            return;
        }

        try {
            const payload = {
                name: currentGroup.name,
                description: currentGroup.description,
                contacts: currentGroup.contacts || [],
            };

            const res = await GroupService.createGroup(payload);
            setShowEditModal(false);
            await fetchGroups(true); // Force refresh after create
            alert(res?.data?.message || "Group created successfully");
        } catch (e) {
            alert(e.response?.data?.message || "Failed to create group");
        }
    };

    const handleUpdate = async () => {
        if (!currentGroup) return;

        if (!currentGroup.name || !currentGroup.description) {
            alert("Please fill in all required fields");
            return;
        }

        try {
            const payload = {
                groupId: currentGroup.id,
                name: currentGroup.name,
                description: currentGroup.description,
                contacts: currentGroup.contacts || [],
            };

            const res = await GroupService.updateGroup(payload);
            setShowEditModal(false);
            await fetchGroups(true); // Force refresh after update
            alert(res?.data?.message || "Group updated successfully");
        } catch (e) {
            alert("Failed to update group");
            console.error("Failed to update group", e);
        }
    };

    const addContact = async () => {
        if (!phoneNumber.trim() || !contactName.trim() || !contactEmail.trim()) {
            alert("Please enter phone number, contact name and contact email");
            return;
        }

        // Basic email validation
        const trimmedEmail = contactEmail.trim();
        if (!isValidEmail(trimmedEmail)) {
            alert("Please enter a valid email address");
            return;
        }

        // Basic phone number validation
        const phoneRegex = /^\+?[1-9]\d{1,14}$/;
        const cleanPhone = phoneNumber.replace(/\s+/g, '');
        if (!phoneRegex.test(cleanPhone)) {
            alert("Please enter a valid phone number (e.g., +1234567890)");
            return;
        }

        // Check for duplicate phone number
        const existingContacts = currentGroup?.contacts || [];
        if (existingContacts.some(c => c.phoneNumber === cleanPhone)) {
            alert("This phone number is already in the group");
            return;
        }

        if (addingContact) return;

        try {
            setAddingContact(true);

            const firstName = contactName.split(' ')[0] || contactName;
            const lastName = contactName.split(' ').slice(1).join(' ') || 'N.A';
            const leadPayload = {
                firstName: firstName || '',
                lastName: lastName || '',
                email: contactEmail.trim(),
                WhatsAppNo: cleanPhone,
                countryCode: "+91"
            };

            const res = await GroupService.addLead(leadPayload);
            if (res?.data?.statusCode !== API_STATUS.SUCCESS && res?.data?.statusCode !== API_STATUS.CREATED) {
                alert(res?.data?.message || "Failed to add lead");
                return;
            }

            const newContact = {
                id: Date.now(),
                phoneNumber: cleanPhone,
                name: contactName.trim(),
                email: contactEmail.trim(),
            };

            setCurrentGroup((prev) => ({
                ...prev,
                contacts: [...(prev.contacts || []), newContact],
            }));

            setPhoneNumber("");
            setContactName("");
            setContactEmail("");
            fetchLeads(true); // Force refresh after adding lead
            alert(res?.data?.message || "Lead added successfully");
        } catch (e) {
            console.error("Failed to add lead", e);
            alert(e?.response?.data?.message || "Failed to add lead");
        } finally {
            setAddingContact(false);
        }
    };

    const removeContact = (contactId) => {
        setCurrentGroup((prev) => ({
            ...prev,
            contacts: (prev.contacts || []).filter((c) => c.id !== contactId),
        }));
    };

    const handleImportCSV = async () => {
        // Create a file input element
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.csv';
        input.onchange = async (e) => {
            const file = e.target.files[0];
            if (!file) return;

            const reader = new FileReader();
            reader.onload = async (event) => {
                try {
                    const text = event.target.result;
                    const lines = text.split('\n').filter(line => line.trim());
                    if (lines.length < 2) {
                        alert("CSV file must contain at least a header row and one data row");
                        return;
                    }

                    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());

                    // Find column indices - flexible matching
                    const nameIndex = headers.findIndex(h =>
                        h.includes('name') || h.includes('contact name')
                    );
                    const emailIndex = headers.findIndex(h =>
                        h.includes('email')
                    );
                    const phoneIndex = headers.findIndex(h =>
                        h.includes('phone') || h.includes('number')
                    );

                    if (emailIndex === -1 || phoneIndex === -1) {
                        alert("CSV must contain 'email' and 'phoneNumber' columns");
                        return;
                    }

                    // Parse CSV and build payload
                    const bulkLeadPayload = [];
                    const newContacts = [];
                    const existingPhoneNumbers = new Set(
                        (currentGroup?.contacts || []).map(c => c.phoneNumber?.toString())
                    );

                    for (let i = 1; i < lines.length; i++) {
                        const values = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
                        const name= values[nameIndex] || '';
                        const email = values[emailIndex] || '';
                        const phoneNumber = values[phoneIndex] || '';
                        const firstName = name.split(' ')[0] || name;
                        const lastName = name.split(' ').slice(1).join(' ') || 'N.A';

                        // Clean phone number (remove spaces, keep digits and +)
                        const cleanPhoneNumber = phoneNumber.replace(/\s+/g, '');

                        // Validate email and phone number
                        if (email && cleanPhoneNumber && isValidEmail(email)) {
                            // Build API payload
                            bulkLeadPayload.push({
                                firstName: firstName || '',
                                lastName: lastName || '',
                                email: email,
                                WhatsAppNo: cleanPhoneNumber,
                            });

                            // Build contact for group (if not duplicate)
                            if (!existingPhoneNumbers.has(cleanPhoneNumber)) {
                                newContacts.push({
                                    id: Date.now() + i,
                                    phoneNumber: cleanPhoneNumber,
                                    name: name,
                                    email: email,
                                });
                                existingPhoneNumbers.add(cleanPhoneNumber);
                            }
                        }
                    }

                    if (bulkLeadPayload.length === 0) {
                        alert("No valid contacts found in CSV. Please check the format.");
                        return;
                    }

                    // Call insertBulkLead API
                    try {
                        const res = await GroupService.insertBulkLead(bulkLeadPayload);
                        if (
                            res.data.statusCode === API_STATUS.SUCCESS ||
                            res.data.statusCode === API_STATUS.CREATED
                        ) {
                            // Add contacts to current group
                            if (newContacts.length > 0) {
                                setCurrentGroup((prev) => ({
                                    ...prev,
                                    contacts: [...(prev.contacts || []), ...newContacts],
                                }));
                            }
                            fetchLeads(true); // Force refresh after import
                            alert(
                                res?.data?.message || 
                                `Successfully imported ${bulkLeadPayload.length} lead(s). ${newContacts.length > 0 ? `${newContacts.length} contact(s) added to group.` : ''}`
                            );
                        } else {
                            alert(res?.data?.message || "Failed to import leads from CSV");
                        }
                    } catch (apiError) {
                        console.error("Error calling insertBulkLead API", apiError);
                        alert(apiError?.response?.data?.message || "Failed to import leads. Please try again.");
                    }
                } catch (error) {
                    console.error("Error parsing CSV", error);
                    alert("Failed to parse CSV file. Please check the format.");
                }
            };
            reader.readAsText(file);
        };
        input.click();
    };

    const formatDate = (dateString) => {
        if (!dateString) return "";
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                month: 'numeric',
                day: 'numeric',
                year: 'numeric',
            });
        } catch (e) {
            return dateString;
        }
    };

    return (
        <div className="py-3">
            <Row className="mb-3">
                <Col>
                    <h3 className="fw-bold">Broadcast Groups</h3>
                    <div className="text-muted">
                        Create and manage contact groups for broadcasting WhatsApp messages.
                    </div>
                </Col>
                <Col className="text-end d-flex justify-content-end align-items-start gap-2">
                    <Button variant="primary" onClick={openCreateModal}>
                        + Create Group
                    </Button>
                </Col>
            </Row>

            <Card>
                <Card.Body>
                    <Form onSubmit={handleSearchSubmit} className="mb-3">
                        <Form.Control
                            type="text"
                            title="Press Enter to search"
                            placeholder="Search groups (min 3 characters)..."
                            value={search}
                            onChange={handleSearchChange}
                        />
                        {isSearchTooShort(search) ? (
                            <Form.Text className="text-danger">
                                Enter at least {MIN_SEARCH_LENGTH} characters to search.
                            </Form.Text>
                        ) : null}
                    </Form>

                    {loading && (
                        <div className="text-center text-muted py-4">Loading...</div>
                    )}

                    {!loading && groups.length === 0 && (
                        <div className="text-center text-muted py-4">
                            No groups found.
                        </div>
                    )}

                    {!loading && groups.length > 0 && (
                        <Row className="g-3">
                            {groups.map((group) => (
                                <Col key={group.id} md={6} lg={4}>
                                    <Card className="h-100">
                                        <Card.Body>
                                            <div className="d-flex align-items-start justify-content-between mb-3">
                                                <div className="d-flex align-items-center gap-2">
                                                    <div
                                                        style={{
                                                            width: "40px",
                                                            height: "40px",
                                                            borderRadius: "50%",
                                                            backgroundColor: "#B2400B",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            color: "white",
                                                        }}
                                                    >
                                                        <FaUsers size={20} />
                                                    </div>
                                                    <div>
                                                        <h5 className="mb-0 fw-bold">
                                                            {group.name}
                                                        </h5>
                                                    </div>
                                                </div>
                                                <div className="d-flex gap-2">
                                                    <Button
                                                        variant="link"
                                                        size="sm"
                                                        className="p-0"
                                                        onClick={() => openEditModal(group)}
                                                    >
                                                        <FiEdit2 />
                                                    </Button>
                                                    <Button
                                                        variant="link"
                                                        size="sm"
                                                        className="p-0 text-danger"
                                                        onClick={() => handleDelete(group)}
                                                    >
                                                        <RiDeleteBinLine />
                                                    </Button>
                                                </div>
                                            </div>

                                            <div className="mb-2 text-muted small">
                                                {group.contactCount} contact
                                                {group.contactCount !== 1 ? "s" : ""}
                                            </div>

                                            <div className="mb-2 text-muted">
                                                {group.description}
                                            </div>

                                            <div className="text-muted small">
                                                Created: {formatDate(group.createdAt)}
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    )}

                    {!loading && groups.length > 0 && (
                        <div className="d-flex justify-content-between align-items-center mt-3">
                            <div className="text-muted small">
                                Page {page} of {totalPages}
                            </div>
                            <div className="d-flex gap-2">
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    disabled={page <= 1}
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                >
                                    Previous
                                </Button>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    disabled={page >= totalPages}
                                    onClick={() =>
                                        setPage((p) =>
                                            totalPages
                                                ? Math.min(totalPages, p + 1)
                                                : p + 1
                                        )
                                    }
                                >
                                    Next
                                </Button>
                            </div>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* Create / Edit Modal */}
            <Modal
                show={showEditModal}
                onHide={() => setShowEditModal(false)}
                size="lg"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {editMode === "create" ? "Create Group" : "Edit Group"}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
                    {currentGroup && (
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    Group Name <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control
                                    value={currentGroup.name}
                                    onChange={(e) =>
                                        setCurrentGroup((prev) => ({
                                            ...prev,
                                            name: e.target.value,
                                        }))
                                    }
                                    placeholder="e.g., VIP Customers"
                                />
                            </Form.Group>

                            <Form.Group className="mb-2">
                                <Form.Label>
                                    Description <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={currentGroup.description}
                                    onChange={(e) =>
                                        setCurrentGroup((prev) => ({
                                            ...prev,
                                            description: e.target.value,
                                        }))
                                    }
                                    placeholder="Brief description of this group"
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <div className="d-flex justify-content-between align-items-center mb-1 flex-wrap gap-2">
                                    <Form.Label className="mb-0">
                                        Contacts ({currentGroup.contacts?.length || 0})
                                    </Form.Label>
                                    <div className="d-flex gap-2 align-items-center">
                                        <div className="d-flex align-items-center gap-2">
                                            <MultiSelect
                                                options={leads.map((lead) => ({
                                                    value: lead.leadId,
                                                    label: lead.name || lead.phoneNumber || `Lead ${lead.leadId}`,
                                                }))}
                                                selectedValues={selectedLeadIds}
                                                onChange={handleLeadSelection}
                                                placeholder="Select leads as contact"
                                                disabled={loadingLeads || leads.length === 0}
                                                loading={loadingLeads}
                                            />
                                            {selectedLeadIds.length > 0 && (
                                                <Button
                                                    variant="outline-success"
                                                    size="sm"
                                                    onClick={addSelectedLeadsAsContacts}
                                                >
                                                    Add ({selectedLeadIds.length})
                                                </Button>
                                            )}
                                        </div>
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            className="d-inline-flex align-items-center justify-content-center flex-nowrap gap-2"
                                            style={{ whiteSpace: "nowrap" }}
                                            onClick={handleImportCSV}
                                        >
                                            <LuImport size={16} className="flex-shrink-0" aria-hidden />
                                            <span>Import CSV</span>
                                        </Button>
                                    </div>
                                </div>

                                <Row className="g-2 mb-1">
                                    <Col md={4}>
                                        <Form.Control
                                            type="text"
                                            placeholder="Phone number (e.g., +1234567890)"
                                            value={phoneNumber}
                                            onChange={(e) => setPhoneNumber(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    addContact();
                                                }
                                            }}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <Form.Control
                                            type="text"
                                            placeholder="Contact name"
                                            value={contactName}
                                            onChange={(e) => setContactName(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    addContact();
                                                }
                                            }}
                                        />
                                    </Col>
                                     <Col md={4}>
                                        <Form.Control
                                            type="text"
                                            placeholder="Email"
                                            value={contactEmail}
                                            onChange={(e) => setContactEmail(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    addContact();
                                                }
                                            }}
                                        />
                                    </Col>
                                </Row>

                                <Button
                                    variant="secondary"
                                    className="w-100 mb-3"
                                    onClick={addContact}
                                    disabled={addingContact}
                                >
                                    {addingContact ? "Adding..." : "Add Contact"}
                                </Button>

                                {currentGroup.contacts && currentGroup.contacts.length > 0 ? (
                                    <div className="border rounded p-2" style={{ maxHeight: "200px", overflowY: "auto" }}>
                                        {currentGroup.contacts.map((contact) => (
                                            <div
                                                key={contact.id}
                                                className="d-flex justify-content-between align-items-center p-2 border-bottom"
                                            >
                                                <div>
                                                    <div className="fw-bold">{contact.name}</div>
                                                    <div className="text-muted small">
                                                        {contact.phoneNumber}
                                                    </div>
                                                </div>
                                                <Button
                                                    variant="link"
                                                    size="sm"
                                                    className="text-danger p-0"
                                                    onClick={() => removeContact(contact.id)}
                                                >
                                                    <RiDeleteBinLine />
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center text-muted py-3 border rounded">
                                        No contacts added yet
                                    </div>
                                )}
                            </Form.Group>
                        </Form>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowEditModal(false)}
                    >
                        Cancel
                    </Button>
                    {editMode === "create" && (
                        <Button variant="primary" onClick={handleSave}>
                            Create Group
                        </Button>
                    )}
                    {editMode === "edit" && (
                        <Button variant="primary" onClick={handleUpdate}>
                            Update Group
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default Group;
